import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CHANGE_INVENTORY,
  EMOTIONAL_AVOID,
  EMOTIONAL_FEEL,
  FLEX_LEVELS,
  FRICTION_PHASES,
  OUTCOME_PLACEHOLDERS,
  OWNERSHIP_TYPES,
  type DdwConfig,
} from '../config';
import { calcBaselineTax, findRedFlagHits, fmtMoney } from '../calc';
import type { DdwLens, FlexibilityLevel } from '../types';
import { SidebarSummary } from './SidebarSummary';

interface Props {
  lens: DdwLens;
  config: DdwConfig;
  initialStep?: number;
  onChange(updater: (lens: DdwLens) => DdwLens): void;
  onFinish(): void;
}

/**
 * Phase 1 — six-step lens wizard. Each step is conditionally rendered
 * but state lives in the lens object passed down from the parent so
 * persistence is handled in one place. Sticky right-rail sidebar shows
 * the lens-in-progress.
 */
export function Phase1Wizard({ lens, config, initialStep = 1, onChange, onFinish }: Props) {
  const [step, setStep] = useState(initialStep);

  // Recompute baseline tax whenever the lens tax inputs change.
  useEffect(() => {
    const total = Math.round(calcBaselineTax(lens, config.calc).total);
    if (total !== lens.tax.calculatedAnnual) {
      onChange((prev) => ({ ...prev, tax: { ...prev.tax, calculatedAnnual: total } }));
    }
  }, [lens, config, onChange]);

  const valid = useMemo(() => stepIsValid(step, lens), [step, lens]);

  const goNext = () => {
    if (!valid) return;
    if (step < 6) {
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: 'instant' });
    } else {
      onFinish();
    }
  };
  const goBack = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  };

  return (
    <div className="ddw-wizard-wrap">
      <div className="ddw-wizard-grid">
        <div>
          <div className="ddw-progress">
            <div className="ddw-progress-meta">
              <span className="ddw-progress-label">Phase 1 · Build your lens</span>
              <span className="ddw-progress-count">Step {step} of 6</span>
            </div>
            <div className="ddw-progress-track">
              <div className="ddw-progress-fill" style={{ width: `${(step / 6) * 100}%` }} />
            </div>
          </div>

          <div className="ddw-step-card">
            {step === 1 && <Step1JobStatement lens={lens} config={config} onChange={onChange} />}
            {step === 2 && <Step2Friction lens={lens} onChange={onChange} />}
            {step === 3 && <Step3Outcomes lens={lens} onChange={onChange} />}
            {step === 4 && <Step4Emotional lens={lens} onChange={onChange} />}
            {step === 5 && <Step5FlexibilityTax lens={lens} config={config} onChange={onChange} />}
            {step === 6 && <Step6Ownership />}
          </div>

          <div className="ddw-wizard-nav">
            <button
              type="button"
              className="ddw-wizard-nav-btn ddw-wizard-nav-btn--back"
              onClick={goBack}
              disabled={step === 1}
            >
              ← Back
            </button>
            <button
              type="button"
              className="ddw-wizard-nav-btn ddw-wizard-nav-btn--next"
              onClick={goNext}
              disabled={!valid}
            >
              {step === 6 ? 'Finish lens →' : 'Continue →'}
            </button>
          </div>
        </div>

        <SidebarSummary lens={lens} onJumpToStep={setStep} />
      </div>
    </div>
  );
}

// ─── Validation ──────────────────────────────────────────────

function stepIsValid(step: number, lens: DdwLens): boolean {
  if (step === 1) return lens.jobStatement.trim().length >= 10;
  if (step === 2) return !!lens.frictionPhase;
  if (step === 3) return lens.outcomes.filter((o) => o && o.trim().length > 0).length === 3;
  if (step === 4) return lens.emotional.feel.length >= 1 && lens.emotional.avoid.length >= 1;
  if (step === 5) {
    return (
      !!lens.flexibility &&
      lens.tax.routine >= 0 &&
      lens.tax.workarounds >= 0 &&
      lens.tax.major >= 0
    );
  }
  return true; // step 6 is educational
}

// ─── Step 1 — Job Statement ──────────────────────────────────

interface StepProps {
  lens: DdwLens;
  onChange: Props['onChange'];
}

function Step1JobStatement({
  lens,
  config,
  onChange,
}: StepProps & { config: DdwConfig }) {
  // Debounce the red-flag check (500ms after last keystroke).
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleInput = (val: string) => {
    onChange((prev) => ({ ...prev, jobStatement: val }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const hits = findRedFlagHits(val, config.redFlagWords);
      onChange((prev) => ({ ...prev, redFlagWordsHit: hits }));
    }, 500);
  };

  // Fire once on mount in case state was hydrated mid-text.
  useEffect(() => {
    const hits = findRedFlagHits(lens.jobStatement, config.redFlagWords);
    if (hits.join('|') !== lens.redFlagWordsHit.join('|')) {
      onChange((prev) => ({ ...prev, redFlagWordsHit: hits }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hits = lens.redFlagWordsHit;

  return (
    <>
      <span className="ddw-step-tag">Step 1 of 6 · Job Statement</span>
      <h2 className="ddw-step-title">What progress is your team trying to make?</h2>
      <p className="ddw-step-sub">
        Describe the job you're hiring software to do — stated in a way that would be true whether
        you used software, paper processes, or nothing at all. This single sentence becomes the spine
        of your evaluation.
      </p>

      <div className="ddw-field">
        <label className="ddw-field-label" htmlFor="ddw-job">
          Your job statement
        </label>
        <p className="ddw-field-hint">
          Try to describe the outcome, not the tool. Example: "Enable the business to execute
          agreements quickly while protecting the company from unacceptable risk."
        </p>
        <textarea
          id="ddw-job"
          className="ddw-textarea"
          rows={3}
          value={lens.jobStatement}
          onChange={(e) => handleInput(e.target.value)}
          placeholder="Enable the business to execute agreements quickly while protecting the company from unacceptable risk."
        />
        {hits.length > 0 && (
          <div className="ddw-coach" role="status">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
              />
            </svg>
            <div>
              <strong>Your statement names a solution.</strong> You used:{' '}
              {hits
                .slice(0, 3)
                .map((h) => `"${h}"`)
                .join(', ')}
              . Try restating in terms of the progress you're trying to make — that keeps the door
              open to options you haven't considered yet.
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Step 2 — Friction Phase ─────────────────────────────────

function Step2Friction({ lens, onChange }: StepProps) {
  return (
    <>
      <span className="ddw-step-tag">Step 2 of 6 · Friction</span>
      <h2 className="ddw-step-title">Where does the work break down today?</h2>
      <p className="ddw-step-sub">
        Most evaluation teams chase symptoms. Picking the phase where the job actually breaks down
        focuses your evaluation on what will move the needle.
      </p>

      <div className="ddw-field">
        <label className="ddw-field-label">The phase that creates the most friction</label>
        <p className="ddw-field-hint">
          If multiple are painful, pick the one that costs you the most time, money, or political
          capital.
        </p>
        <div className="ddw-btngroup ddw-btngroup--cols-2" role="radiogroup" aria-label="Friction phase">
          {FRICTION_PHASES.map((p) => (
            <button
              type="button"
              key={p.key}
              role="radio"
              aria-checked={lens.frictionPhase === p.key}
              className={`ddw-pill${lens.frictionPhase === p.key ? ' is-selected' : ''}`}
              onClick={() => onChange((prev) => ({ ...prev, frictionPhase: p.key }))}
            >
              <span>{p.label}</span>
              <span className="ddw-pill-sub">{p.sub}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── Step 3 — Outcomes ───────────────────────────────────────

function Step3Outcomes({ lens, onChange }: StepProps) {
  const setOutcome = (idx: 0 | 1 | 2, val: string) => {
    onChange((prev) => {
      const out = [...prev.outcomes] as DdwLens['outcomes'];
      out[idx] = val;
      return { ...prev, outcomes: out };
    });
  };

  return (
    <>
      <span className="ddw-step-tag">Step 3 of 6 · Outcomes</span>
      <h2 className="ddw-step-title">What three outcomes would tell you it's working?</h2>
      <p className="ddw-step-sub">
        These become your scoring criteria. Phrase each as a measurable change — start with "Reduce"
        or "Increase" and name the thing you'd watch.
      </p>

      <div className="ddw-field">
        {[0, 1, 2].map((i) => (
          <div className="ddw-outcome-row" key={i}>
            <span className="ddw-outcome-prefix">Outcome {i + 1}</span>
            <input
              type="text"
              className="ddw-input"
              value={lens.outcomes[i] ?? ''}
              onChange={(e) => setOutcome(i as 0 | 1 | 2, e.target.value)}
              placeholder={OUTCOME_PLACEHOLDERS[i]}
            />
          </div>
        ))}
        <p className="ddw-field-hint" style={{ marginTop: 12 }}>
          Best when phrased as <em>"Reduce X"</em> or <em>"Increase Y"</em> with something
          measurable. Not enforced — just a hint.
        </p>
      </div>
    </>
  );
}

// ─── Step 4 — Emotional ──────────────────────────────────────

function Step4Emotional({ lens, onChange }: StepProps) {
  const toggle = (bucket: 'feel' | 'avoid', item: string) => {
    onChange((prev) => {
      const arr = prev.emotional[bucket];
      const next = arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
      return { ...prev, emotional: { ...prev.emotional, [bucket]: next } };
    });
  };

  return (
    <>
      <span className="ddw-step-tag">Step 4 of 6 · How it should feel</span>
      <h2 className="ddw-step-title">When this is working, what should your team feel — and stop feeling?</h2>
      <p className="ddw-step-sub">
        Functional fixes don't drive adoption — emotional ones do. The features can be perfect; if
        the system makes attorneys feel surveilled or makes your team look slow to the business, it
        gets quietly abandoned. This is where the real adoption decisions live.
      </p>

      <div className="ddw-field">
        <label className="ddw-field-label">Your team should feel and be perceived as…</label>
        <p className="ddw-field-hint">
          Pick all that matter. These become evaluation criteria — vendors that move you toward
          these score better.
        </p>
        <div className="ddw-emo-grid">
          {EMOTIONAL_FEEL.map((item) => {
            const selected = lens.emotional.feel.includes(item);
            return (
              <label key={item} className={`ddw-emo-chip${selected ? ' is-selected is-feel' : ''}`}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggle('feel', item)}
                  aria-label={item}
                />
                <span>{item}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="ddw-field">
        <label className="ddw-field-label">…and should stop feeling</label>
        <p className="ddw-field-hint">
          The pain you want this evaluation to make go away. Lost nights. Burnout. Reputational
          hits. Be honest — the things you don't say out loud are usually the ones that drive
          adoption.
        </p>
        <div className="ddw-emo-grid">
          {EMOTIONAL_AVOID.map((item) => {
            const selected = lens.emotional.avoid.includes(item);
            return (
              <label key={item} className={`ddw-emo-chip${selected ? ' is-selected is-avoid' : ''}`}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggle('avoid', item)}
                  aria-label={item}
                />
                <span>{item}</span>
              </label>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ─── Step 5 — Flexibility & Tax ──────────────────────────────

function Step5FlexibilityTax({
  lens,
  config,
  onChange,
}: StepProps & { config: DdwConfig }) {
  const setFlex = (key: FlexibilityLevel) => {
    onChange((prev) => ({
      ...prev,
      flexibility: key,
      changeInventoryTicked: key !== 'low' ? [] : prev.changeInventoryTicked,
    }));
  };
  const toggleInventory = (key: string) => {
    onChange((prev) => {
      const arr = prev.changeInventoryTicked;
      const next = arr.includes(key) ? arr.filter((x) => x !== key) : [...arr, key];
      return { ...prev, changeInventoryTicked: next };
    });
  };
  const setTax = (k: 'routine' | 'workarounds' | 'major', val: string) => {
    const n = Math.max(0, parseInt(val, 10) || 0);
    onChange((prev) => ({ ...prev, tax: { ...prev.tax, [k]: n } }));
  };

  const baseline = calcBaselineTax(lens, config.calc).total;
  const inv = lens.changeInventoryTicked;

  return (
    <>
      <span className="ddw-step-tag">Step 5 of 6 · Flexibility & Cost</span>
      <h2 className="ddw-step-title">How much does your system need to bend?</h2>
      <p className="ddw-step-sub">
        Most legal departments underestimate how much their organization changes. The system that
        fits perfectly today is the one that becomes a workaround factory in eighteen months.
      </p>

      <div className="ddw-field">
        <label className="ddw-field-label">Your flexibility requirement</label>
        <p className="ddw-field-hint">
          How much organizational change do you expect in the next 24 months?
        </p>
        <div className="ddw-btngroup ddw-btngroup--cols-3" role="radiogroup" aria-label="Flexibility level">
          {FLEX_LEVELS.map((f) => (
            <button
              type="button"
              key={f.key}
              role="radio"
              aria-checked={lens.flexibility === f.key}
              className={`ddw-pill${lens.flexibility === f.key ? ' is-selected' : ''}`}
              onClick={() => setFlex(f.key)}
            >
              <span>{f.label}</span>
              <span className="ddw-pill-sub">{f.sub}</span>
            </button>
          ))}
        </div>

        {lens.flexibility === 'low' && (
          <div className="ddw-reveal">
            <div className="ddw-reveal-title">Quick check — anything below likely in 24 months?</div>
            <div className="ddw-checkgrid">
              {CHANGE_INVENTORY.map((c) => (
                <label key={c.key} className="ddw-check">
                  <input
                    type="checkbox"
                    checked={inv.includes(c.key)}
                    onChange={() => toggleInventory(c.key)}
                  />
                  <span>{c.label}</span>
                </label>
              ))}
            </div>
            {inv.length >= 3 && (
              <div style={{ marginTop: 12, fontSize: 12, color: 'var(--warning)' }}>
                You ticked {inv.length}. Consider whether <strong>Medium</strong> or <strong>High</strong>{' '}
                is the more honest answer.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="ddw-field">
        <label className="ddw-field-label">Adaptation tax — quick estimate</label>
        <p className="ddw-field-hint">
          Three numbers. We'll estimate what your current setup costs you per year. Defaults are
          typical for a mid-sized legal team.
        </p>

        <div className="ddw-num-row">
          <div className="ddw-num-cell">
            <div className="ddw-num-cell-label">Routine changes per year</div>
            <div className="ddw-num-cell-hint">New hires, departures, role changes</div>
            <div className="ddw-num-wrap">
              <input
                type="number"
                className="ddw-num"
                min={0}
                max={500}
                value={lens.tax.routine}
                onChange={(e) => setTax('routine', e.target.value)}
              />
              <span className="ddw-num-suffix">/yr</span>
            </div>
          </div>
          <div className="ddw-num-cell">
            <div className="ddw-num-cell-label">Workarounds maintained today</div>
            <div className="ddw-num-cell-hint">Manual steps, spreadsheets, hacks</div>
            <input
              type="number"
              className="ddw-num"
              min={0}
              max={200}
              value={lens.tax.workarounds}
              onChange={(e) => setTax('workarounds', e.target.value)}
            />
          </div>
          <div className="ddw-num-cell">
            <div className="ddw-num-cell-label">Major changes next 12 mo.</div>
            <div className="ddw-num-cell-hint">Reorgs, M&A, regulatory mandates</div>
            <input
              type="number"
              className="ddw-num"
              min={0}
              max={20}
              value={lens.tax.major}
              onChange={(e) => setTax('major', e.target.value)}
            />
          </div>
        </div>

        <div className="ddw-calc-display" aria-live="polite">
          <div>
            <div className="ddw-calc-display-label">Estimated annual adaptation tax</div>
            <div className="ddw-calc-display-meta">
              Industry benchmark for a mid-sized legal team: $
              {(config.calc.benchmarkLow / 1000).toFixed(0)}K – $
              {(config.calc.benchmarkHigh / 1000).toFixed(0)}K / yr
            </div>
          </div>
          <div className="ddw-calc-display-value">{fmtMoney(baseline)}</div>
        </div>
      </div>
    </>
  );
}

// ─── Step 6 — Ownership (educational only) ───────────────────

function Step6Ownership() {
  return (
    <>
      <span className="ddw-step-tag">Step 6 of 6 · Vendor Ownership</span>
      <h2 className="ddw-step-title">How a vendor is owned predicts how they'll behave.</h2>
      <p className="ddw-step-sub">
        This is the part of evaluation most legal teams skip — and it's the one that explains the
        surprises later: surprise price hikes, support degradation, products quietly stagnating.
        Read this once. In Phase 2, when you enter each vendor's ownership type, the workbench will
        surface the patterns to watch for automatically.
      </p>

      <div className="ddw-field">
        <div className="ddw-owner-grid">
          {OWNERSHIP_TYPES.map((o) => (
            <div key={o.key} className="ddw-owner-card">
              <div className="ddw-owner-card-type">{o.short}</div>
              <div className="ddw-owner-card-label">{o.label}</div>
              <div className="ddw-owner-card-incentive">{o.sub}</div>
            </div>
          ))}
        </div>
        <div className="ddw-owner-note">
          <strong>One question vendors aren't expecting:</strong> "Who owns you, and what's their
          time horizon?" When you score each vendor in the next phase, you'll select their ownership
          type and see the typical behaviors flagged automatically — no flagging required here.
        </div>
      </div>
    </>
  );
}
